import { HttpInterceptorFn } from '@angular/common/http';
import { AuthService } from '../services/auth.service.js';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { decryptToken } from '../../utils/decryptToken.js';
import { getRefreshToken } from '../../utils/httpHelperFunctions.js';
import { showCustomAlert } from '../../utils/alertFunctions.js';
import { Router } from '@angular/router';
import { isProviderUser } from '../guards/auth.guard.js';

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const refreshReq = req.url.includes('refresh');

  const expiryTimeMs = Number(localStorage.getItem('sessionExpiryTime'));
  const currentTime = Math.floor(Date.now() / 1000);
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = getRefreshToken();

  // If no tokens exist, proceed without authentication
  if (!accessToken || !refreshToken) {
    return next(req);
  }

  if (expiryTimeMs) {
    if (currentTime >= expiryTimeMs && !refreshReq) {
      // Session expired, attempt to refresh
      return authService.refreshToken(refreshToken).pipe(
        switchMap((response: any) => {
          localStorage.setItem('access_token', response.access_token);
          localStorage.setItem('refresh_token', response.refresh_token);
          const decryptedToken = decryptToken(response.access_token);
          localStorage.setItem('sessionExpiryTime', decryptedToken.exp);

          const newReq = req.clone({
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              Authorization: `Bearer ${response.access_token}`,
            }),
          });

          return next(newReq);
        }),
        catchError((error: HttpErrorResponse) => {
          console.log('REFRESH ERROR', error);
          // Determine redirect before clearing storage - isProviderUser relies on it
          const redirectPath = isProviderUser() ? '/provider' : '/';

          // Clear invalid tokens
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('sessionExpiryTime');

          // Redirect to the role-appropriate login when the refresh token is too old or invalid
          router.navigate([redirectPath]);

          showCustomAlert(
            'Session Expired',
            'Please login again to continue using the app.',
            'warning',
            false,
            'center'
          );
          return throwError(() => error);
        })
      );
    } else {
      // Session is valid, add Authorization header if not already present
      const hasAuthHeader = req.headers.has('Authorization');
      if (!hasAuthHeader) {
        const newReq = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${accessToken}`)
        });
        return next(newReq);
      }
      return next(req);
    }
  } else {
    // No expiry time set, add Authorization header if not already present
    const hasAuthHeader = req.headers.has('Authorization');
    if (!hasAuthHeader) {
      const newReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessToken}`)
      });
      return next(newReq);
    }
    return next(req);
  }
};
